import { z } from 'zod';
import { CreditResolutionMethod, CrewClaimResolutionMethodEnum, CrewClaimRollupStatusCode, CrewClaimTypeEnum, FinalizationEmailTypeEnum, } from '../../enums/crew.js';
import { CurrencyCode } from '../../enums/currency.js';
import { enumToZodLiteralUnion, helperSchema } from '../../zodExtensions.js';
import { stripeConfig } from '../customer/schema/settings.js';
import { claimAddress, claimAddressCreate, orderAddress } from './address.js';
import { claimLineItem, claimLineItemCreate, finalizationLineItemPayload, } from './claimLineItem.js';
import { claimCustomerPaymentStatus } from './claimCustomerPaymentStatus.js';
import { fingerprintCreate } from './claimRiskAssessment.js';
import { sharedClaimSourceUnion } from './claimSource.js';
import { storeOrderLineItem } from './lineItem.js';
import { shipment, shipmentCreate } from './shipment.js';
import { claimTimeline } from './timeline.js';
export const tag = z.object({
    id: z.number(),
    name: z.string(),
});
export const tagWithoutId = tag.omit({ id: true });
const orderLineItemMetadataMinium = storeOrderLineItem.pick({
    id: true,
    idFromPlatform: true,
    variantIdFromPlatform: true,
    imgUrl: true,
    name: true,
    quantity: true,
    unitPrice: true,
    unitTax: true,
    sku: true,
    optionsFromPlatform: true,
});
const resolvedRefund = z.object({
    id: z.number(),
    amount: z.number(),
    shippingFee: z.number(),
    handlingFee: z.number(),
    createdOn: z.string(),
    isRefundPending: z.boolean(),
    wasResolvedExternally: z.boolean(),
});
const resolvedExchangeOrder = z.object({
    id: z.number(),
    idFromPlatform: z.string(),
    orderNameFromPlatform: z.string(),
    createdOn: z.string(),
    lineItems: z.array(orderLineItemMetadataMinium),
    platformAdminUrl: z.string(),
    wasResolvedExternally: z.boolean(),
    isOriginalOrder: z.boolean(),
    wasShopNow: z.boolean().optional(),
});
const resolutionSummary = z.object({
    refunds: z.array(resolvedRefund),
    //! crewClaimGiftCard table
    giftCards: z.array(z.object({
        id: z.number(),
        amount: z.number(),
        incentiveAmount: z.number(),
        createdOn: z.string(),
        platformAdminUrl: z.string().optional(),
        orderNameFromPlatform: z.string().optional(),
        wasResolvedExternally: z.boolean(),
        type: z
            .union([
            z.literal('Gift_Card'),
            z.literal('Gift_Card_Order'),
            z.literal('Store_Credit'),
        ])
            .optional(),
    })),
    //! crewClaimExchangeOrder table
    exchangeOrders: z.array(resolvedExchangeOrder.extend({
        lineItems: z.array(orderLineItemMetadataMinium.extend({
            productIdFromPlatform: z.string().optional(),
            // a line can fulfill several claim line items when they exchange into the same variant
            claimLineItemIds: z.array(z.number()),
        })),
    })),
});
export const shippingClaimResolutionSummary = z.object({
    refund: resolvedRefund.optional(),
    // `id` is the reorder row, so the store order is carried separately for tracking lookups
    exchangeOrder: resolvedExchangeOrder
        .extend({ storeOrderId: z.number() })
        .optional(),
});
export const claimCustomerPayment = z.object({
    id: z.number(),
    idFromPlatform: z.string(),
    amount: z.number(), // ! non refundable amount (IE, fees, shipping, etc.)
    // only recorded for refunds issued after the column existed; older refunds are assumed to be the whole capture
    refundedAmount: z.number().optional(),
    note: z.string().optional(),
    currencyCode: z.string(),
    status: claimCustomerPaymentStatus,
    authorizationExpiresOn: z.string().nullable(),
    capturedOn: z.string().nullable(),
});
// * claim payment (collected through stripe)
export const claimCustomerPaymentCreate = z
    .object({
    idFromPlatform: z.string(),
    amount: z.number(), // ! non refundable amount (IE, fees, shipping, etc.)
    authorizedAmount: z.number().optional(), // ! refundable amount (IE, anything that is not a fee, the value of the instant exchange)
    currencyCode: z.string(),
})
    .merge(z.object({ stripeConfig }));
const requestSummary = z
    .object({
    totals: z.object({
        giftCard: z.number(),
        refund: z.number(),
    }),
})
    .merge(z.object({
    approvedLineItems: z.array(claimLineItem
        .pick({
        id: true,
        quantity: true,
        amount: true,
        resolutionMethodEnum: true,
    })
        .extend({
        estimatedAmount: z.number().optional(),
        wasZeroedThroughDeduction: z.boolean().optional(),
    })),
}));
//* claim
export const claim = z.object({
    id: z.number(),
    isGiftReturn: z.boolean().optional(),
    customerId: z.number().optional(),
    resolutionSummary: resolutionSummary.optional(), // TODO make non-optional
    externalId: z.string(),
    linkToCustomerUi: z.string().optional(),
    returnIdFromPlatform: z.string().nullable(),
    source: sharedClaimSourceUnion.optional(),
    // ? Since everything is assumed store, this is also assumed store
    currencyCode: z.enum(CurrencyCode),
    // todo: consolidate with order.ts storeOrderClaim
    originalStoreOrder: z.object({
        id: z.number(),
        idFromPlatform: z.string(),
        customerIdFromPlatform: z.string().optional(),
        orderNo: z.string().optional(),
        orderNumber: z.string().optional(),
        currencyCode: z.string(),
        createdOn: z.string(),
        platformAdminUrl: z.string().optional(),
        customerCurrencyConversionRate: z.number().optional(),
        wasCreatedFromRegistration: z.boolean(),
        isExchangeOfExchange: z.boolean(),
        taxRate: z.number().optional(),
        customerEmail: z.string().optional(),
        shippingAddress: orderAddress.optional(),
        registration: z
            .object({
            id: z.number(),
            externalId: z.string(),
            proofOfPurchaseFileUrls: z.array(z.string()),
            registrationChannel: z.string().optional(),
        })
            .optional(),
    }),
    // whether or not the original order was modified, by the addition of one or more new line items
    // related to the new exchange functionality
    wasOriginalOrderModified: z.boolean(),
    isInstantExchange: z.boolean(),
    noteToCustomer: helperSchema.optionalizedString,
    customerEmail: z.string(),
    /**
     * @deprecated used customer.firstName + customer.lastName
     */
    customerName: z.string().optional(),
    customer: z
        .object({
        firstName: z.string().optional(),
        lastName: z.string().optional(),
    })
        .optional(),
    shippingAddress: claimAddress,
    billingAddress: claimAddress,
    claimRollup: z.object({
        name: z.string(),
        code: enumToZodLiteralUnion(CrewClaimRollupStatusCode),
    }),
    timeline: claimTimeline.optional(),
    // * total fee amount applied to the claim, to be used on a new order or deducted from a refund
    //* optional handling fee for refunds
    feeApplied: z.number(),
    feeName: z.string().optional(),
    returnShippingAmountApplied: z.number(),
    exchangeOrderShippingAmountApplied: z.number(), // ! this can be deducted from a refund // or collected with stripe
    // * gift card  incentive offered
    giftCardIncentiveAmountApplied: z.number(),
    // * conditional shipping amount charged based on store_setting of orderValueThresholdForFreeShipping applies to NEW ORDER (variant_exchange)
    shopNowIncentiveAmountApplied: z.number().optional(), // * incentive amount applied to the claim, to be used on a new order
    // how credit goes back to the customer when a line does not say; absent on claims created before it was recorded
    aggregateCreditResolutionMethod: z.enum(CreditResolutionMethod).optional(),
    customerPayment: claimCustomerPayment.optional(),
    // ? rename to claimTypeEnum to be consistent with other enum names
    claimType: enumToZodLiteralUnion(CrewClaimTypeEnum),
    claimLineItems: z.array(claimLineItem),
    //* claim return shipments are associated with a line_item, adding them here elevates the concept that a claim can have multiple shipments spread across N number of line items
    //* for all intents and purposes the length of this array should be 1, until we build out the ability to split out line items into separate return shipments
    claimReturnShipments: z.array(shipment),
    globalEOrderId: z.string().optional(),
    createdOn: z.string(),
    updatedOn: z.string().optional(),
    finalizedOn: z.string().optional(),
    requestSummary: requestSummary.optional(),
    tags: z.array(z.string()).optional(),
});
export const claimListItem = claim
    .pick({
    id: true,
    externalId: true,
    createdOn: true,
    customerEmail: true,
    claimType: true,
})
    .merge(z.object({
    claimRollupStatusCode: enumToZodLiteralUnion(CrewClaimRollupStatusCode),
    customerName: z.string(),
    resolutionMethods: z.array(enumToZodLiteralUnion(CrewClaimResolutionMethodEnum)),
    lineItemHasError: z.boolean(),
    tags: z.string().array(),
    storeId: z.number(),
    storeName: z.string(),
}));
export const claimList = z.array(claimListItem);
export const claimListCount = z.object({ count: z.number() });
export const claimCreate = claim
    .omit({
    claimReturnShipments: true,
    id: true,
    globalEOrderId: true,
    isInstantExchange: true,
    externalId: true,
    updatedOn: true,
    createdOn: true,
    claimLineItems: true,
    claimRollup: true,
    originalStoreOrder: true,
    noteToCustomer: true,
    timeline: true,
    customerPayment: true,
    returnIdFromPlatform: true,
    shippingAddress: true,
    billingAddress: true,
    wasOriginalOrderModified: true,
    requestSummary: true,
    resolutionSummary: true,
    currencyCode: true,
})
    .merge(z.object({
    /** @deprecated Need to unify claim source schemas. */ // TODO unify claim source schemas
    source: z.union([
        z.literal('Customer_App'),
        z.literal('Merchant_App'),
        z.literal('Kustomer_App_Integration'),
        z.literal('Zendesk_App_Integration'),
    ]),
    originalStoreOrderId: z.number(),
    claimLineItems: z.array(claimLineItemCreate).min(1),
    customerPayment: claimCustomerPaymentCreate.optional(),
    shippingAddress: claimAddressCreate,
    billingAddress: claimAddressCreate.optional(),
    fingerprint: fingerprintCreate.optional(),
    shipment: shipmentCreate.shape.returnShipment.optional(),
    shopNowId: z.string().optional(),
}));
export const claimUpdateCustomerInfo = claim.pick({
    id: true,
    shippingAddress: true,
    customerEmail: true,
});
export const claimUpdateFees = claim.pick({
    feeApplied: true,
    returnShippingAmountApplied: true,
    exchangeOrderShippingAmountApplied: true,
});
// ? on claim finalization if you want to notify the customer by email, you can pass in the following payload
export const claimNotifyByEmail = z.object({
    emailType: enumToZodLiteralUnion(FinalizationEmailTypeEnum),
    subject: z.string(),
    header: z.string(),
    body: z.string(),
});
export const claimProcess = claim
    .pick({
    id: true,
    noteToCustomer: true,
    giftCardIncentiveAmountApplied: true,
    feeApplied: true,
    returnShippingAmountApplied: true,
    billingAddress: true,
    shippingAddress: true,
    customerEmail: true,
    externalId: true,
    exchangeOrderShippingAmountApplied: true,
    originalStoreOrder: true,
    claimType: true,
    createdOn: true,
    returnIdFromPlatform: true,
    wasOriginalOrderModified: true,
    globalEOrderId: true,
    tags: true,
})
    .extend({
    claimLineItems: finalizationLineItemPayload.shape.claimLineItems,
});
export const claimFinalizePayload = z.object({
    notifyCustomerByEmail: claimNotifyByEmail.optional(),
    originOrder: z
        .object({
        idFromPlatform: z.string(),
        name: z.string().nullable(),
    })
        .optional(),
    claim: claimProcess,
    /** @deprecated Need to unify claim source schemas. */ // TODO unify claim source schemas
    finalizationSource: z.union([
        z.literal('Merchant_App'),
        z.literal('Kustomer_App_Integration'),
        z.literal('Zendesk_App_Integration'),
    ]),
});
export const claimNeedsMoreInfoPayload = z.object({
    notifyCustomerByEmail: claimNotifyByEmail,
    claim: claimProcess.pick({
        shippingAddress: true,
        externalId: true,
        customerEmail: true,
        id: true,
    }),
});
//# sourceMappingURL=claim.js.map